import React from 'react';

import ChevronLeftLargeIcon from '@atlaskit/icon/glyph/chevron-left-large';
import ChevronRightLargeIcon from '@atlaskit/icon/glyph/chevron-right-large';

import { TestExecutionIssue } from '@qality/common-components';
import { AdjoiningTestCaseInCycle } from '@qality/common-model';

import { useFetchTestCaseIssue } from '../useFetchTestCaseIssue';
import { useTestCaseExecutionNavigation } from '../useTestCaseExecutionNavigation';
import { useTestCycleNavigation } from '../useTestCycleNavigation';
import {
  NoPermissionTextStyled,
  TestExecutionNavigationButtonStyled,
} from './TestExecutionCycleNavigationButton.styled';

type Props = {
  direction: 'previous' | 'next';
  testCaseInCycle: AdjoiningTestCaseInCycle;
  testCycleId: number;
};

export default function TestExecutionCycleNavigationButton({
  direction,
  testCaseInCycle,
  testCycleId,
}: Props): JSX.Element {
  const { navigateToTestExecution } =
    useTestCaseExecutionNavigation(testCycleId);
  const { navigateToTestCycle } = useTestCycleNavigation();

  const { testCaseIssue, isLoading, errorType } = useFetchTestCaseIssue(
    testCaseInCycle.testCaseId
  );

  const getButtonContentAndAction = (): {
    handleButtonClick: () => void;
    InnerComponent: React.ReactNode;
  } => {
    if (errorType === 'no-permission') {
      return {
        handleButtonClick: () => navigateToTestCycle(testCycleId),
        InnerComponent: (
          <NoPermissionTextStyled>
            Test case without permission [Go back to test cycle]
          </NoPermissionTextStyled>
        ),
      };
    }

    return {
      handleButtonClick: () =>
        navigateToTestExecution(testCaseIssue, testCaseInCycle),
      InnerComponent: (
        <TestExecutionIssue
          testCaseIssue={testCaseIssue}
          executionStatus={testCaseInCycle?.status}
          testType={testCaseInCycle.testType}
        />
      ),
    };
  };

  if (isLoading) {
    return null;
  }

  if (errorType === 'unexpected') {
    return null;
  }

  const { InnerComponent, handleButtonClick } = getButtonContentAndAction();

  return (
    <TestExecutionNavigationButtonStyled
      onClick={handleButtonClick}
      data-testid='navigation-button'
    >
      {direction === 'previous' && (
        <ChevronLeftLargeIcon testId='chevron-left-large-icon' label='' />
      )}
      {InnerComponent}
      {direction === 'next' && (
        <ChevronRightLargeIcon testId='chevron-right-large-icon' label='' />
      )}
    </TestExecutionNavigationButtonStyled>
  );
}
